import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { ResetPasswordRequest } from '../types/api';
import LanguageSelector from './LanguageSelector';
import './Login.css'; // Reuse login styles
import reforged1024 from '../assets/reforged-1024x330.png';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<ResetPasswordRequest>({
    email: '',
    token: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const email = searchParams.get('email');
    const token = searchParams.get('token');
    
    if (email && token) {
      setFormData(prev => ({
        ...prev,
        email: decodeURIComponent(email),
        token: decodeURIComponent(token),
      }));
    } else {
      setErrors([t('invalidResetLink')]);
    }
  }, [searchParams, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear errors when user starts typing
    if (errors.length > 0) setErrors([]);
    if (message) setMessage('');
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.newPassword) {
      newErrors.push(t('newPasswordRequired'));
    } else if (formData.newPassword.length < 6) {
      newErrors.push(t('passwordMinLength'));
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.push(t('passwordMismatch'));
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors([]);
    setMessage('');

    try {
      const response = await apiService.resetPassword(formData);
      
      if (response.success) {
        setMessage(t('passwordResetSuccessfully'));
        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        if (response.errors && response.errors.length > 0) {
          setErrors(response.errors);
        } else {
          setErrors([response.message || t('passwordResetFailed')]);
        }
      }
    } catch (err: any) {
      console.error('Reset password error:', err);
      const errorData = err.response?.data;
      if (errorData?.errors && errorData.errors.length > 0) {
        setErrors(errorData.errors);
      } else {
        setErrors([
          errorData?.message || 
          err.message || 
          t('errorResettingPassword')
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  const isFormValid = formData.email && 
                     formData.token && 
                     formData.newPassword.length >= 6 && 
                     formData.confirmPassword.length >= 6;

  return (
    <div className="login-container">
      <div className="login-logo">
        <img src={reforged1024} alt="Reforged Training Zone" className="logo-image" />
      </div>
      <div className="login-card">
        <div className="login-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span></span>
            <LanguageSelector />
          </div>
          <h2>{t('resetPasswordTitle')}</h2>
          <p>{t('enterYourNewPassword')}</p>
          {formData.email && (
            <p className="reset-email">{t('forEmail')} {formData.email}</p>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="newPassword">{t('newPassword')} *</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              required
              minLength={6}
              disabled={isLoading}
              placeholder={t('enterNewPasswordPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('confirmNewPassword')} *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              minLength={6}
              disabled={isLoading}
              placeholder={t('confirmNewPasswordPlaceholder')}
            />
          </div>
          
          {errors.length > 0 && (
            <div className="error-message">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}

          {message && (
            <div className="success-message">
              {message}
              <br />
              <small>{t('redirectingToLogin')}</small>
            </div>
          )}
          
          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            className="login-button"
          >
            {isLoading ? t('resettingPassword') : t('resetPasswordTitle')}
          </button>
        </form>
        
        <div className="login-footer">
          <div className="login-links">
            <button 
              type="button" 
              className="link-button" 
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              {t('backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 